/* eslint-disable max-lines */
import {Lang} from 'yougile-lang'
import {HTMLUtil, logger, ObjUtil, Time, Util, UUID} from 'yougile-util'
import {
  Chat as ChatModel,
  ChatMessage,
  DataObjectId,
  DataObjectProperties,
  extensionPublicHubTitlePrefix,
  GroupChat,
  groupChatContainerId,
  Hub,
  HubLike,
  HubProperties,
  isDataObjectId,
  MessageTextChunk,
  MessageTextParams,
  newsCompanyId,
  noBoardContainerId,
  rootNodeId,
  SandboxTree,
  sharingGuestAccount,
  StorageDepartmentHierarchy,
  User,
  youGileUserId
} from 'yougile-model'
import {Chat} from '@api/model/chat'
import {MessageObj} from '@api/model/message-obj'
import {UserObj} from '@api/model/user-obj'
import {Accounts} from '@client/accounts'
import {ClientLogger} from '@client/client-logger'
import {ClientUtilities} from '@client/client-utilities'
import {DepartmentsClient} from '@client/departments-client'
import {DirectMessages} from '@client/direct-messages'
import {EventBus} from '@client/event-bus'
import {minimizeChatEvent, restoreChatEvent} from '@client/events'
import {GroupChatsClient} from '@client/group-chats-client'
import {PermissionsChecker} from '@client/permissions-checker'
import {SharedBoardsClient} from '@client/shared-boards-client'
import {Sync} from '@client/sync'
import {SyncEvents} from '@client/sync-events'
import {UserSettings} from '@client/user-settings'
import {Backend} from '@common/backend'
import {FilePreview} from '@common/file-preview'
import {Tracking} from '@common/tracking'
import {TrackingEvent} from '@common/tracking-events'
import {showConfirm} from '@components/common/base/notifications'
import {FileUploadProgress} from '@components/file-upload-progress'
import {MessageBox} from '@components/message-box'
import {rightPanelStore} from '@components/right-panel/right-panel/right-panel-store'
import {ChatKind} from '@containers/hooks'
import {ChatSubscribers} from '@extensions/chat-subscribers'
import {EmojiUtils} from '@extensions/emoji-utils'
import {Feedback} from '@extensions/feedback'
import {NotificationSettingsService} from '@extensions/notifications-settings-service'
import {ChatFilterState, TaskChatTab} from './app-persistence'
import {
  getLastMentionMessage,
  getLastSeenMessageTime,
  getUnseenMentions,
  getUnseenMessages
} from './chat'
import {ChatsGroupedByUser, KindOfTaskParticipation} from './chat-types'
import {Company} from './company'
import {globals} from './globals-base'
import {idTasksApi} from './id-tasks/id-tasks-store'
import {MessageNotifier} from './message-notifier'
import {squashExcessMirrors} from './mirrored-tasks/squash-excess-mirrors'
import {PageService} from './page-service'
import {ChatProxy} from './proxies/chat-proxy'
import {GroupChatProxy} from './proxies/group-chat-proxy'
import {PersonalChatProxy} from './proxies/personal-chat-proxy'
import {
  RankedTask,
  TaskChatProxy,
  TaskChatProxyService
} from './proxies/task-chat-proxy'
import {SystemEntities} from './system-entities'
import {Task} from './task'

interface PostMessage {
  hub: Hub
  message: string
  params?: MessageTextParams
  messageToReply?: ChatMessage
  properties?: DataObjectProperties
}

interface UpdateMessage extends PostMessage {
  editedMessageTimestamp: number
}

interface PostOrUpdateMessage extends PostMessage {
  editedMessageTimestamp?: number
  isSystemUser?: boolean
}

interface PostOrUpdateMessageWithFiles extends PostOrUpdateMessage {
  files: File[]
  messageFileLinks?: string[]
}

export type TaskChatsFilterOptions = {
  participation: KindOfTaskParticipation | '*'
  ignoredChatHubId?: DataObjectId
}

export const panelRegexp = /^.*#(?:(chat)|(direct)|(news))(:(\w|-)+)?$/
export const chatRegexp = /^.*#(?:chat|direct|news):(\w)+$/
export const idTaskRegexp = /^.*#(\w+)-(\d)+$/
export const idTaskRegexpSimple =
  /(?<=[\(\[ >])([a-z]\w*)-(\d+)(?=[^a-z0-9а-яйё])/gi

export const isPanel = (url: string): boolean => {
  if (idTaskRegexp.test(url)) {
    return true
  }

  return panelRegexp.test(url)
}

export const isChat = (url: string): boolean => {
  if (idTaskRegexp.test(url)) {
    return true
  }

  return chatRegexp.test(url)
}

const filterPrivateTasks = (
  tasks: Hub[],
  allowPrivateTasks: boolean
): Hub[] => {
  if (allowPrivateTasks) {
    return tasks
  }
  return tasks.filter(task => !task.data.private)
}

export class ChatUtils {
  private static openedChat: Hub | undefined
  private static chatExpanded = true
  private static initialized = false
  private static chatChangeListeners: Map<UUID, () => void> = new Map()

  private static cachedNewMessagesInOtherCompanies: Map<DataObjectId, number> =
    new Map()

  private static cachedTasksGroupedByUserPresence: Map<
    DataObjectId,
    ChatsGroupedByUser
  > = new Map()

  private static cachedDependentTasks: Map<
    DataObjectId,
    Map<DataObjectId, DataObjectId[]>
  > = new Map()

  /**
   * This method is passed from chat implementation.
   * Chat is paged and after this method is called chat should
   * show more messages and then call the specified callback.
   */
  public static requestMore:
    | undefined
    | ((cb: (available: number, total: number) => void) => void)

  public static init(): void {
    const hub = ChatUtils.getChatFromCurrentUrl()
    if (hub) {
      if (PageService.getHash().includes('msg:')) {
        const msgId = PageService.getHash()
          .split('msg:')
          .pop()
          ?.split(',')
          .shift()
        if (msgId) {
          globals.openedChatNoScroll = true
          setTimeout(() => {
            globals.openedChatNoScroll = false
            const parent = document.querySelector('.chat-item-messages')
            ChatUtils.goToId(parent as HTMLElement, msgId)
          }, 2000)
        }
      }
      ChatUtils.openedChat = hub
      rightPanelStore.setCurrentChat(hub.id, hub.companyId)
    }
    ChatUtils.initialized = true
    ChatUtils.chatChangeListeners.forEach((cb: () => void) => cb())

    SyncEvents.addAnyListener(companyId => {
      ChatUtils.cachedDependentTasks.delete(companyId)
      ChatUtils.cachedTasksGroupedByUserPresence.delete(companyId)
      if (companyId === Sync.profileId) {
        ChatUtils.cachedNewMessagesInOtherCompanies.clear()
      } else {
        ChatUtils.cachedNewMessagesInOtherCompanies.delete(companyId)
      }
    })
    globals.onCompanyChange(() =>
      ChatUtils.cachedNewMessagesInOtherCompanies.clear()
    )
  }

  static resetCache(
    type: 'cachedTasksGroupedByUserPresence',
    companyId?: DataObjectId
  ): void {
    if (type === 'cachedTasksGroupedByUserPresence') {
      ChatUtils.cachedTasksGroupedByUserPresence.delete(
        companyId || globals.getCompanyId()
      )
    }
  }

  static onLocationChange(): boolean {
    const newHub = ChatUtils.getChatFromCurrentUrl()
    if (!newHub) {
      ChatUtils.openedChat = undefined
      rightPanelStore.resetCurrentChat()
      ChatUtils.chatChangeListeners.forEach((cb: () => void) => cb())
    } else if (
      !ChatUtils.chatExpanded ||
      !ChatUtils.openedChat ||
      ChatUtils.openedChat.id !== newHub.id
    ) {
      ChatUtils.openedChat = newHub
      rightPanelStore.setCurrentChat(newHub.id, newHub.companyId)
      ChatUtils.chatExpanded = true
      ChatUtils.chatChangeListeners.forEach((cb: () => void) => cb())
    }

    return true
  }

  public static addChatChangeListener(cb: () => void, firstRun = true): UUID {
    const id = Util.UUID()
    ChatUtils.chatChangeListeners.set(id, cb)
    if (ChatUtils.initialized && firstRun) {
      cb()
    }
    return id
  }

  public static removeChatChangeListener(listenerId?: UUID): void {
    listenerId && ChatUtils.chatChangeListeners.delete(listenerId)
  }

  public static getChatFromCurrentUrl(): Hub | undefined {
    const hash = PageService.getHash()
    if (hash.includes('direct:')) {
      const shortId = hash.split('direct:').pop()?.split(',').shift()
      if (!shortId) {
        return undefined
      }
      return Sync.getProfile().findByShortId(shortId) as Hub
    } else if (hash.includes('news:')) {
      const shortId = hash.split('news:').pop()?.split(',').shift()
      if (!shortId) {
        return undefined
      }
      return Sync.newsSandbox?.findByShortId(shortId) as Hub
    } else if (hash.includes('chat:')) {
      const shortId = hash.split('chat:').pop()?.split(',').shift()
      if (!shortId) {
        return undefined
      }
      return Sync.getCurrentSandbox()?.findByShortId(shortId) as Hub
    } else if (hash.toLowerCase().includes('-')) {
      const idTaskRaw = hash.replace('#', '').toLowerCase()

      const indexHyphen = idTaskRaw.lastIndexOf('-')
      const prefix = idTaskRaw.slice(0, indexHyphen)
      const idTask = idTaskRaw.slice(indexHyphen + 1)

      const taskId = idTasksApi.getReverseIdTask(idTask, prefix || 'ID')
      return Sync.getCurrentSandbox()?.getHub(taskId)
    } else {
      return undefined
    }
  }

  public static getDependentTasks(box: SandboxTree, from: Hub): Hub[]
  public static getDependentTasks(
    box: SandboxTree | undefined,
    from: Hub | undefined
  ): Hub[] | undefined {
    if (!box || !from) {
      return undefined
    }
    const cache = ChatUtils.cachedDependentTasks.get(box.companyId)
    if (cache) {
      return (cache.get(from.id) || [])
        .map(id => box.get(id) as Hub)
        .filter(h => !!h)
    }
    const tree: Map<DataObjectId, DataObjectId[]> = new Map()
    Task.forEachTask(box, 'ignore-departments', task => {
      if (task.data.dependencies) {
        ObjUtil.forEach(task.data.dependencies, (id: DataObjectId) => {
          if (!tree.has(id)) {
            tree.set(id, [])
          }
          tree.get(id)?.push(task.id)
        })
      }
    })
    ChatUtils.cachedDependentTasks.set(box.companyId, tree)
    return (tree.get(from.id) || [])
      .map(id => box.get(id) as Hub)
      .filter(
        h => !!h && !h.deleted && !box.getAllParentHubs(h).find(p => p.deleted)
      )
  }

  public static openPersonalChats(track = true): void {
    PageService.setHash('direct')
    if (track) {
      void Tracking.send(TrackingEvent.chatPanelOpenDirectList)
    }
    ChatUtils.restoreChat()
  }

  public static openTaskChats(): void {
    PageService.setHash('chat')
    void Tracking.send(TrackingEvent.chatPanelOpenTaskList)
    ChatUtils.restoreChat()
  }

  public static restoreChat(): void {
    EventBus.dispatch(restoreChatEvent())
  }

  public static openChat(
    hub: Hub,
    props?: {
      stayClosed?: boolean
      messageTime?: number
      tab?: TaskChatTab
      autofocus?: boolean
    }
  ): void {
    const isExtensionComments = hub.title.startsWith(
      extensionPublicHubTitlePrefix
    )

    const {messageTime, tab} = props ?? {}

    if (isExtensionComments) {
      globals.openedChatNoScroll = true
      setTimeout(() => {
        const parent = document.querySelector('.extension-comments')
        ChatUtils.goToId(parent as HTMLElement, 'chat-msg-' + messageTime)
        globals.openedChatNoScroll = false
      }, 400)
      return
    }

    if (!props?.stayClosed) {
      rightPanelStore.setAutofocus(props?.autofocus)
      ChatUtils.restoreChat()
    }

    const box = Sync.get(hub)
    logger.log([hub, box])
    if (
      box.companyId !== Sync.profileId &&
      box.companyId !== newsCompanyId &&
      globals.getCompanyId() !== box.companyId
    ) {
      Company.setCurrent(box.companyId, true)
      const project = box.getProjectFor(hub)
      const validProj =
        !!project && !project.title.startsWith('.') && !project.deleted

      const projPath = validProj ? box.calculatePath(project) : '/'

      void (async () => {
        const newHash = await ChatUtils.getChatHash(hub)
        const newUrl = projPath + '#' + newHash
        // const newUrl = projPath + '#chat:' + Util.shortId(hub.id)

        if (newUrl !== PageService.getUrl()) {
          PageService.setUrl(newUrl)
        }
      })()
    } else {
      void (async () => {
        const newHash = await ChatUtils.getChatHash(hub)
        if (newHash !== PageService.getHash()) {
          PageService.setHash(newHash)
        }
      })()
    }

    if (tab) {
      globals.openedChatNoScroll = true
      setTimeout(() => {
        rightPanelStore.setTab(tab, props?.autofocus)
        globals.openedChatNoScroll = false
      }, 400)
    } else if (messageTime) {
      globals.openedChatNoScroll = true
      setTimeout(() => {
        rightPanelStore.setTab('chat', props?.autofocus)
        const parent = document.querySelector('.chat-item-messages')
        ChatUtils.goToId(parent as HTMLElement, 'chat-msg-' + messageTime)
        globals.openedChatNoScroll = false
      }, 400)
    }
  }

  private static async getChatHash(hub: Hub): Promise<string> {
    const box = Sync.get(hub)
    let prefix = {
      [Sync.profileId as string]: 'direct:',
      [newsCompanyId]: 'news:'
    }[box.companyId]

    // old hash version
    if (
      GroupChat.isGroupChat(hub) ||
      Task.isRepeatTemplate(hub) ||
      Task.isMovementTemplate(hub) ||
      Task.isTaskTemplate(hub)
    ) {
      prefix = 'chat:'
    }

    if (prefix) {
      return prefix + Util.shortId(hub.id)
    }

    const idTask = idTasksApi.getIdTask(hub.id)
    if (idTask) {
      return idTask
    }

    // for custom hubs that generated in a unusual way
    return 'chat:' + Util.shortId(hub.id)
  }

  public static scrollBottom(e: HTMLElement): void {
    if (e) {
      e.scrollTop = e.scrollHeight
    }
  }

  public static scrollTop(e: HTMLElement): void {
    if (e) {
      e.scrollTop = 0
    }
  }

  public static closeChatIfOpened(): void {
    EventBus.dispatch(minimizeChatEvent())
    PageService.setHash('')
  }

  public static openChatById(taskId: DataObjectId): void {
    const task = Task.getById(taskId)
    const canShow = PermissionsChecker.checkShow('ignore-departments', task, {})
    if (task && canShow) {
      ChatUtils.openChat(task)
    } else {
      MessageBox.warn(Lang.msg.subtask.noPermissionForTask)
    }
  }

  public static toggleChat(
    task?: Hub,
    props?: {
      messageTime?: number
      tab?: TaskChatTab
    }
  ): void {
    if (task) {
      if (
        ChatUtils.isChatExpanded() &&
        ChatUtils.openedChat &&
        ChatUtils.openedChat.id === task.id
      ) {
        ChatUtils.closeChatIfOpened()
      } else {
        ChatUtils.openChat(task, props)
      }
    } else {
      if (ChatUtils.isChatExpanded()) {
        ChatUtils.closeChatIfOpened()
      } else if (ChatUtils.openedChat) {
        ChatUtils.openChat(ChatUtils.openedChat, props)
      }
    }
  }

  public static isChatExpanded(): boolean {
    return rightPanelStore.state.chatPanel.opened
  }

  public static getOpenedChatHub(): Hub | undefined {
    return ChatUtils.openedChat
  }

  public static removeOpenedChatHub(): void {
    ChatUtils.openedChat = undefined
    rightPanelStore.resetCurrentChat()
    ChatUtils.updateListeners()
  }

  public static setOpenedTaskChat(id: DataObjectId): void {
    ChatUtils.openedChat = Sync.getCurrentSandbox().get(id) as Hub
    rightPanelStore.setCurrentChat(id, ChatUtils.openedChat.companyId)
    ChatUtils.updateListeners()
  }

  public static setOpenedPersonalChat(id: DataObjectId): void {
    const companyId = Sync.profileId
    if (!companyId) {
      return
    }
    ChatUtils.openedChat = Sync.getProfile().get(id) as Hub
    rightPanelStore.setCurrentChat(id, companyId)
    ChatUtils.updateListeners()
  }

  public static setOpenedNewsChat(id: DataObjectId): void {
    ChatUtils.openedChat = Sync.newsSandbox?.get(id) as Hub
    rightPanelStore.setCurrentChat(id, newsCompanyId)
    ChatUtils.updateListeners()
  }

  public static setOpenedFeedbackChat(id: DataObjectId): void {
    ChatUtils.openedChat = Sync.getFeedbackCompany().get(id) as Hub
    rightPanelStore.setCurrentChat(id, ChatUtils.openedChat.companyId)
    ChatUtils.updateListeners()
  }

  private static updateListeners(): void {
    ChatUtils.chatChangeListeners.forEach((cb: () => void) => cb())
  }

  public static goToLabel(label: string, containerSelector?: string): void {
    void Tracking.send(TrackingEvent.chatFollowQuicklink)
    const parent = document.querySelector(
      containerSelector ?? '.chat-item-messages'
    )
    ChatUtils.goToId(parent as HTMLElement, ChatUtils.toChatLabelId(label))
  }

  public static toChatLabelId(x: any): string {
    return (
      'chat-label-' +
      window.btoa(encodeURIComponent(x.toString())).replace(/=/g, '')
    )
  }

  public static goToId(
    parent: HTMLElement,
    elemId: string,
    {
      disallowExpand = false,
      recur = 0,
      shouldHighlight = true
    }: {
      disallowExpand?: boolean
      recur?: number
      shouldHighlight?: boolean
    } = {}
  ): void {
    if (!parent) {
      return
    }

    const anchor = parent.querySelector('#' + elemId)

    if (!anchor) {
      if (!disallowExpand && recur < 200 && ChatUtils.requestMore) {
        parent.scrollTop = 0
        ChatUtils.requestMore((avail, total) => {
          ChatUtils.goToId(parent, elemId, {
            disallowExpand: avail >= total,
            recur: recur + 1,
            shouldHighlight
          })
        })
      } else if (recur >= 200) {
        Backend.report(
          'Goto reached limit',
          globals.currentUserId,
          ChatUtils.openedChat?.id ?? 'undefined',
          elemId
        )
      }
      return
    }

    const oldScroll = parent.scrollTop
    const anchorTop = anchor.getBoundingClientRect().top
    const msgCntTop = parent.getBoundingClientRect().top
    parent.scrollTop = oldScroll + anchorTop - msgCntTop - 50

    if (shouldHighlight) {
      let parent: Element | null = anchor
      while (parent) {
        if (
          parent.classList.contains('message-item-new') ||
          parent.classList.contains('can-be-highlighted')
        ) {
          parent.classList.add('highlighted', 'group/highlighted')
          const savedParent = parent
          setTimeout(
            () =>
              savedParent.classList.remove('highlighted', 'group/highlighted'),
            700
          )
          break
        }
        parent = parent.parentElement
      }
    }
  }

  public static findChatForCurrentUser(chatOrUserId: DataObjectId): Hub {
    const user = Sync.getProfile().get(chatOrUserId)
    if (user && user instanceof User) {
      return DirectMessages.requestChatWith(user.id)
    } else {
      return (
        (Sync.getCurrentSandbox().get(chatOrUserId) as Hub) ||
        (Sync.getProfile().get(chatOrUserId) as Hub) ||
        (Sync.newsSandbox && (Sync.newsSandbox.get(chatOrUserId) as Hub))
      )
    }
  }

  public static taskChatOrderComparer(first: Hub, second: Hub): number {
    const {taskChatOrder: order} = ChatUtils
    return order(first) - order(second)
  }

  public static personalChatOrderComparer(first: User, second: User): number {
    const {personalChatOrder: order} = ChatUtils
    return order(first) - order(second)
  }

  public static getSplitChatLocation(
    hub: Hub,
    type: 'task' | 'column' = 'task'
  ): string[] {
    if (!hub) {
      return []
    }
    if (hub.location === rootNodeId) {
      return []
    }
    if (hub.companyId === Sync.profileId) {
      return []
    } else {
      const box = Sync.get(hub)
      const location = ChatUtils.getLocationByType(hub, type)
      const group = box.get(location)
      if (!group) {
        return []
      }
      if (group.location === rootNodeId) {
        return []
      }
      const parent = box.get(box.get(group.location).location)
      if (!parent) {
        return []
      }
      if (group.id === group.title) {
        return []
      }
      return [parent.title, box.get(group.location).title, group.title]
    }
  }

  private static getLocationByType(
    hub: Hub,
    type: 'task' | 'column'
  ): DataObjectId {
    if (type === 'task') {
      return hub.location
    }

    return type === 'column' ? hub.id : ('' as DataObjectId)
  }

  public static getChatLocation(hublike: HubLike): string {
    const hub = Task.find(hublike)
    return ChatUtils.getSplitChatLocation(hub).join(' » ')
  }

  public static getUserRealNameForDisplay(
    userId: DataObjectId,
    task?: Hub
  ): string {
    return Sync.getCurrentUser().id === userId
      ? Lang.msg.you
      : Accounts.getName(userId, task)
  }

  public static taskChatOrder(chatHub: Hub): number {
    const message = chatHub.chat.lastMessage()
    return message ? -message.timestamp : 0
  }

  public static personalChatOrder(user: User): number {
    const status = ['online', 'offline'].indexOf(Accounts.get(user.id).status)
    const hub = DirectMessages.getChatIfExists(user.id)
    const lm = hub?.chat?.length ? hub.chat.lastMessage() : undefined
    const lastMessage = lm ? 3 * lm.timestamp : (2 - status) * user.timestamp
    return -lastMessage
  }

  /**
   * Number of new messages from both personal and task chats
   * in the company that is not equal to current company,
   * i.e. personal chats that are already present in the current company
   * are not taken into account.
   */
  public static allNewMessagesInOtherCompany(companyId: DataObjectId): number {
    const cached = ChatUtils.cachedNewMessagesInOtherCompanies.get(companyId)
    if (cached !== undefined) {
      return cached
    }
    const box = Sync.get({companyId})
    const currentBox = Sync.getCurrentSandbox()
    if (!box || !currentBox) {
      return 0
    }
    const taskChatMessages =
      ChatUtils.newMessagesFromAllTaskChats(box, 'withCurrentUser') +
      ChatUtils.newMessagesFromAllGroupChats(box)
    const currentUsers: Set<DataObjectId> = new Set([globals.currentUserId])
    currentBox.getAllUsers(false).forEach(u => currentUsers.add(u.id))
    const personalChatMessages = box
      .getAllUsers(false)
      .filter(u => !currentUsers.has(u.id))
      .map(ChatUtils.newMessagesFromPersonalChat)
      .reduce((a, b) => a + b, 0)
    const result = taskChatMessages + personalChatMessages
    ChatUtils.cachedNewMessagesInOtherCompanies.set(companyId, result)
    return result
  }

  public static allNewMessagesInOtherCompanies(): number {
    let res = 0
    Company.listSandboxes().forEach(box => {
      if (box.companyId !== globals.getCompanyId()) {
        res += ChatUtils.allNewMessagesInOtherCompany(box.companyId)
      }
    })
    return res
  }

  public static newMessagesFromChat(chatHub: Hub): number {
    if (!globals.currentUserId) {
      return 0
    }
    const box = Sync.get(chatHub)
    const user = box.get(globals.currentUserId) as User
    const timestamp = user ? user.timestamp : 0
    const notificationSettings =
      NotificationSettingsService.getNotificationSettings()
    return getUnseenMessages({
      userId: globals.currentUserId,
      hub: chatHub,
      startFrom: timestamp,
      includeSystem: true,
      notificationSettings
    })
  }

  public static newMessagesFromAllGroupChats(
    box: SandboxTree,
    ignoredChatHubId?: DataObjectId,
    includeMentioned = false
  ): number {
    const chats = GroupChatsClient.listChats(box, globals.currentUserId).filter(
      h => !!h && h.id !== ignoredChatHubId
    )
    return chats
      .map(chat => {
        if (this.isUserShouldBeNotified(chat, globals.currentUserId)) {
          return ChatUtils.newMessagesFromChat(chat)
        }
        if (includeMentioned && ChatUtils.chatHasUnseenMention(chat)) {
          return getUnseenMentions({
            userId: globals.currentUserId,
            hub: chat,
            startFrom: getLastSeenMessageTime(chat)
          })
        }
        return 0
      })
      .reduce((n, sum) => sum + n, 0)
  }

  private static isUserShouldBeNotified(hub: Hub, userId: string): boolean {
    const users = hub.properties?.[HubProperties.groupChatNotified]
    return typeof users === 'string' && users.includes(`${userId}:1`)
  }

  public static markAsSeen(task: Hub, user?: UserObj): void {
    MessageNotifier.markRead(task.id)
    task.chat.read(user ? user.id : globals.currentUserId)
    task.commit()
  }

  public static getChats(
    box: SandboxTree,
    filter: ChatFilterState = <ChatFilterState>{
      personal: true,
      myTasks: true,
      otherTasks: false,
      groupChats: true,
      privateTasks: true
    }
  ): ChatProxy[] {
    const {companyId} = box
    const tasks = ChatUtils.tasksFilteredByUserPresence(
      box,
      {
        withCurrentUser: filter.myTasks,
        withoutCurrentUser: filter.otherTasks
      },
      filter.privateTasks
    )
      .map(t => TaskChatProxyService.createForCurrentUser(t))
      .map(t => ({...t, withMe: ChatUtils.isMyChat(box, t.chatId)}))

    const userChats = filter.personal
      ? box
          .getAllUsers(false)
          .map(PersonalChatProxy.createForCurrentUser)
          .filter(
            u => u.userId !== globals.currentUserId && u.lastMessageTimestamp
          )
      : []

    const groupChats = filter.groupChats
      ? GroupChatsClient.listChats(box, globals.currentUserId).map(h =>
          GroupChatProxy.create(box, h)
        )
      : []

    const time = (i: TaskChatProxy): number => {
      if (
        i.lastMessageTimestamp &&
        (!i.createdTimestamp || i.lastMessageTimestamp > i.createdTimestamp)
      ) {
        return i.lastMessageTimestamp
      }
      if (ChatUtils.isMyChat(box, i.chatId)) {
        return i.createdTimestamp || 0
      }
      return 0
    }
    const archiveTime = (a: TaskChatProxy): number => {
      return a.complete && a.complete > time(a) && a.newMessagesAmount === 0
        ? a.complete
        : 0
    }
    const unreadValue = (a: TaskChatProxy): number => {
      if (!filter.unreadOnTop) {
        return 0
      }
      const chatHub = ChatUtils.findChatForCurrentUser(a.chatId)
      const sandbox = Sync.get(chatHub)
      if (chatHub && chatHub.location === groupChatContainerId) {
        const isNotified = GroupChat.isNotified(chatHub, globals.currentUserId)
        if (!isNotified && !ChatUtils.chatHasUnseenMention(chatHub)) {
          return 0
        }
      }
      return ChatUtils.isMyChat(sandbox, a.chatId) && a.newMessagesAmount > 0
        ? 1
        : 0
    }
    const starredValue = (a: TaskChatProxy): number =>
      filter.starredOnTop
        ? +a.starred || +UserSettings.isStarred({id: a.chatId, companyId}) || 0
        : 0

    const chats = ([...tasks, ...userChats, ...groupChats] as TaskChatProxy[])
      .map(
        (t): RankedTask => ({
          task: t,
          archive: archiveTime(t),
          starred: starredValue(t),
          unread: unreadValue(t),
          markImportant: t.markImportant || 0,
          time: time(t)
        })
      )
      .sort(
        (a, b) =>
          b.starred - a.starred ||
          b.markImportant - a.markImportant ||
          b.unread - a.unread ||
          (a.archive && b.archive ? 1 : -1) * (b.archive - a.archive) ||
          b.time - a.time ||
          b.task.createdTimestamp - a.task.createdTimestamp
      )
      .map(c => c.task)

    return chats
  }

  public static newMessagesFromAllTaskChats(
    box: SandboxTree,
    participation: KindOfTaskParticipation | '*',
    ignoredChatHubId?: DataObjectId
  ): number {
    const {
      tasksGroupedByUserPresence,
      newMessagesFromChat: newMessagesFromTaskChat
    } = ChatUtils
    const {withCurrentUser, withoutCurrentUser} =
      tasksGroupedByUserPresence(box)

    const chatsWithoutUser =
      participation !== 'withCurrentUser'
        ? withoutCurrentUser
        : withoutCurrentUser.filter(ChatUtils.chatHasUnseenMention)

    const chats = [
      ...(participation !== 'withoutCurrentUser' ? withCurrentUser : []),
      ...chatsWithoutUser
    ]
    const res = ignoredChatHubId
      ? chats.filter(h => h.id !== ignoredChatHubId)
      : chats
    return res.map(newMessagesFromTaskChat).reduce((n, sum) => sum + n, 0)
  }

  public static newMessagesFromPersonalChat(partner: {
    id: DataObjectId
  }): number {
    const {id, timestamp} = Sync.getCurrentUser()
    if (!id) {
      return 0
    }
    const hub = DirectMessages.getChatIfExists(partner.id)
    return hub ? hub.chat.newMessagesFor(id, timestamp) : 0
  }

  public static newMessagesFromPersonalChats(): number {
    return Sync.getCurrentSandbox()
      .getAllUsers(false)
      .reduce((sum, user) => {
        if (!user || user.id === globals.currentUserId) {
          return sum
        }
        return sum + ChatUtils.newMessagesFromPersonalChat(user)
      }, 0)
  }

  public static newMessagesFromAllPersonalChats(
    ignoredUserId?: DataObjectId
  ): number {
    return Sync.getOtherUserIds().reduce((sum, uid) => {
      if (ignoredUserId && uid === ignoredUserId) {
        return sum
      }
      return sum + ChatUtils.newMessagesFromPersonalChat({id: uid})
    }, 0)
  }

  /**
   * Should be used only on client to uniquely identify chat message
   * (shouldn't be saved in database).
   */
  public static calculateMessageId(
    taskId: DataObjectId,
    timestamp: number
  ): DataObjectId {
    return (taskId + '|msg=' + timestamp) as DataObjectId
  }

  public static getMessageById(
    messageId: DataObjectId,
    box: SandboxTree
  ): ChatMessage | undefined {
    const timestamp = messageId.split('|msg=').pop()
    if (!timestamp) {
      return undefined
    }
    return ChatUtils.getTaskFromId(messageId, box)?.chat.messages.get(
      +timestamp
    )
  }

  public static getTaskFromId(
    messageId: DataObjectId,
    box: SandboxTree
  ): Hub | undefined {
    const taskId = messageId.split('|').shift() as DataObjectId
    return box.get(taskId) as Hub | undefined
  }

  public static isMyChat(
    box: SandboxTree,
    id: DataObjectId,
    userId?: DataObjectId
  ): boolean {
    const systemEntities = SystemEntities.get(box.companyId)
    if (!systemEntities) {
      return false
    }
    const hub = box.get(id) as Hub
    if (ChatUtils.getChatKind(hub) === 'personal') {
      return true
    }
    if (hub && hub.location === groupChatContainerId) {
      return !!GroupChat.getParticipants(hub)[userId || globals.currentUserId]
    }
    const notifyTag = systemEntities.notifyTagId
    const mod = box.getModifiers(notifyTag, id)
    return mod?.includes(userId || globals.currentUserId)
  }

  public static kindOfTaskParticipation(
    box: SandboxTree,
    chatHub: Hub
  ): KindOfTaskParticipation {
    return ChatUtils.isMyChat(box, chatHub.id)
      ? 'withCurrentUser'
      : 'withoutCurrentUser'
  }

  public static tasksFilteredByUserPresence(
    box: SandboxTree,
    filter: ChatsGroupedByUser<boolean>,
    allowPrivateTasks: boolean
  ): Hub[] {
    const {withCurrentUser, withoutCurrentUser} =
      ChatUtils.tasksGroupedByUserPresence(box)

    const chatsWithoutUser = filter.withoutCurrentUser
      ? withoutCurrentUser
      : withoutCurrentUser.filter(ChatUtils.chatHasUnseenMention)

    return [
      ...(filter.withCurrentUser
        ? filterPrivateTasks(withCurrentUser, allowPrivateTasks)
        : []),
      ...chatsWithoutUser
    ]
  }

  public static chatHasUnseenMention(hub: Hub): boolean {
    return !!getLastMentionMessage(
      hub,
      globals.currentUserId,
      getLastSeenMessageTime(hub)
    )
  }

  public static isAvailableChat(
    box: SandboxTree,
    taskId: DataObjectId,
    hierarchy: StorageDepartmentHierarchy
  ): boolean {
    const task = box.get(taskId) as Hub
    return PermissionsChecker.checkShow(hierarchy, task, {
      hasNoChildrenHint: true
    })
  }

  public static tasksGroupedByUserPresence(
    box: SandboxTree
  ): Readonly<ChatsGroupedByUser> {
    const cached = ChatUtils.cachedTasksGroupedByUserPresence.get(box.companyId)
    if (cached) {
      return cached
    }

    const withoutCurrentUser: Hub[] = []
    const withCurrentUser: Hub[] = []
    const calculatedHierarchy = DepartmentsClient.getHierarchy(box)

    const checkCache = PermissionsChecker.makeCheckCache()
    const companyHub = box.getHub(box.companyId)
    if (companyHub) {
      box.walkForward(companyHub, (obj, level) => {
        if (obj.id === noBoardContainerId) {
          return true
        }
        if (
          obj.data?.isCreatedByDefault ||
          obj.deleted ||
          obj.title.startsWith('.')
        ) {
          return false
        }
        const isNoBoardTask = obj.location === noBoardContainerId
        if (level === 4 || isNoBoardTask) {
          const isMy = ChatUtils.isMyChat(box, obj.id)
          const permDefault = PermissionsChecker.checkShow(
            'ignore-departments',
            obj,
            {
              ignoreAdmin: true,
              hasNoChildrenHint: true,
              calculatedHierarchy,
              checkCache
            }
          )

          let allowForAdminOnly = false

          if (PermissionsChecker.isAdmin({userId: globals.currentUserId})) {
            obj.data

            const permAdmin = PermissionsChecker.checkShow(
              'ignore-departments',
              obj,
              {
                ignoreAdmin: false,
                hasNoChildrenHint: true,
                calculatedHierarchy,
                checkCache
              }
            )

            if (permAdmin && !permDefault) {
              allowForAdminOnly = true
            }
          }

          const case1 =
            (isMy || obj.chat.messages.size || UserSettings.isStarred(obj)) &&
            permDefault

          const case2 =
            (isMy || UserSettings.isStarred(obj)) && allowForAdminOnly

          if (case1 || case2) {
            if (isMy) {
              withCurrentUser.push(obj)
            } else {
              withoutCurrentUser.push(obj)
            }
          }
        }

        return level < 4 && !isNoBoardTask
      })
    }

    squashExcessMirrors(withCurrentUser)
    squashExcessMirrors(withoutCurrentUser)

    ChatUtils.cachedTasksGroupedByUserPresence.set(box.companyId, {
      withCurrentUser,
      withoutCurrentUser
    })
    return {withCurrentUser, withoutCurrentUser}
  }

  public static chatMessagesCount(hub: Hub): number {
    return hub.chat.messages.size
  }

  public static hasMessage(
    hub: {id: DataObjectId; companyId: DataObjectId},
    messageId: DataObjectId
  ): boolean {
    if (!hub) {
      return false
    }
    const box = Sync.get(hub)
    if (!box) {
      return false
    }
    const chat = box.get(hub.id) as Hub

    return (
      chat && [...chat.chat.messages.values()].some(m => m.id === messageId)
    )
  }

  public static taskChatHubsGroupedByCurrentUserPresence(
    box: SandboxTree
  ): ChatsGroupedByUser {
    const withoutCurrentUser: Hub[] = []
    const withCurrentUser: Hub[] = []
    const calculatedHierarchy = DepartmentsClient.getHierarchy(box)
    const checkCache = PermissionsChecker.makeCheckCache()
    const companyHub = box.getHub(box.companyId)
    companyHub &&
      box.walkForward(companyHub, (obj, level) => {
        if (obj.id === noBoardContainerId) {
          return true
        }
        if (obj.deleted || obj.title.startsWith('.')) {
          return false
        }
        const isNoBoardTask = obj.location === noBoardContainerId
        if (level === 4 || isNoBoardTask) {
          if (
            PermissionsChecker.checkShow('ignore-departments', obj, {
              ignoreAdmin: true,
              hasNoChildrenHint: true,
              calculatedHierarchy,
              checkCache
            })
          ) {
            if (!ChatUtils.isMyChat(box, obj.id)) {
              withoutCurrentUser.push(obj)
            } else {
              withCurrentUser.push(obj)
            }
          }
        }
        return level < 4 && !isNoBoardTask
      })

    return {withCurrentUser, withoutCurrentUser}
  }

  static getChatKind(hub: Hub | undefined): ChatKind {
    // Same checks as in RightPanelContentBase
    if (!hub) {
      return 'empty'
    } else if (GroupChat.isGroupChat(hub)) {
      return 'group'
    } else if (hub.companyId === newsCompanyId) {
      return 'news'
    } else if (Feedback.column && hub.location === Feedback.column.id) {
      return 'feedback'
    } else if (hub.companyId === Sync.profileId) {
      return 'personal'
    } else {
      return 'task'
    }
  }

  static subscribeUsersFromChunks(hub: Hub, chunks: MessageTextChunk[]): void {
    const hierarchy = DepartmentsClient.getHierarchy(Sync.getCurrentSandbox())
    const listForSubs: DataObjectId[] = []

    for (const chunk of chunks) {
      if (
        chunk.type === 'user' &&
        chunk.data.userId &&
        !ChatSubscribers.isSubscribed(chunk.data.userId, hub) &&
        ChatSubscribers.canBeSubscribed(chunk.data.userId, hub, hierarchy) &&
        PermissionsChecker.editWhoToNotifyMode(hub) === 'yes'
      ) {
        listForSubs.push(chunk.data.userId)
      }
    }

    for (let index = 0; index < listForSubs.length; index++) {
      const userId = listForSubs[index]
      const commit = index === listForSubs.length - 1

      ChatSubscribers.subscribe(userId, hub, commit)
    }
  }

  private static postMessage({
    hub,
    message,
    params,
    messageToReply,
    properties
  }: PostMessage): ChatMessage | undefined {
    const {isGroupChat, isDirect} = ChatModel.chatType(hub.toJSON())
    if (isGroupChat) {
      void Tracking.send(TrackingEvent.groupChatSendMessage)
    } else if (isDirect) {
      void Tracking.send(TrackingEvent.directSendMessage)
    } else {
      void Tracking.send(TrackingEvent.taskSendMessage)
    }
    if (SharedBoardsClient.guestId) {
      if (isDataObjectId(SharedBoardsClient.guestId)) {
        void Tracking.send(TrackingEvent.shareUserSendMessage)
      } else {
        void Tracking.send(TrackingEvent.shareGuestSendMessage)
      }
    }
    if (!ChatSubscribers.isSubscribed(globals.currentUserId, hub)) {
      if (PermissionsChecker.editWhoToNotifyMode(hub) !== 'no') {
        const commit = !params?.chunks?.length
        ChatSubscribers.subscribe(globals.currentUserId, hub, commit)
      }
    }

    if (isDirect) {
      const user = DirectMessages.getDirectChatUser(hub)
      if (user) {
        DirectMessages.checkChat(user.id)
      }
    }
    if (EmojiUtils.containsEmoji(message)) {
      void Tracking.send(TrackingEvent.chatEmojiUsed)
    }

    let userId = SharedBoardsClient.getId()
      ? sharingGuestAccount.id
      : globals.currentUserId
    if (!userId) {
      logger.error('No userId found')
      return undefined
    }
    if (properties?.isSystemUser) {
      userId = youGileUserId
    }
    const m = hub.chat.addMessage(
      userId,
      {textHtml: message},
      {noChangeSeenMessages: !!SharedBoardsClient.getId()}
    )
    if (SharedBoardsClient.guestId) {
      m.properties.guestId = SharedBoardsClient.guestId
    }
    if (params?.chunks?.length) {
      ChatUtils.subscribeUsersFromChunks(hub, params.chunks)
      m.properties.params = params
      void Tracking.send(TrackingEvent.chatMentioned, {
        countMentions: params?.chunks?.length
      })
    }

    if (properties) {
      m.properties = {
        ...m.properties,
        ...properties
      }
    }

    if (messageToReply) {
      m.setReplyTo(messageToReply)
      void Tracking.send(TrackingEvent.chatReplied)
    }
    Chat.postMessage(MessageObj.fromMessageAndHub(m, hub))
    ClientLogger.logMessage(hub, m)

    hub.commit()

    return m
  }

  private static updateMessage({
    hub,
    message,
    params,
    editedMessageTimestamp,
    messageToReply
  }: UpdateMessage): void {
    const msg = hub.chat.messages.get(editedMessageTimestamp)
    if (!msg) {
      ClientLogger.skipMessage(
        hub,
        'edited message not found ' + editedMessageTimestamp
      )
      logger.warn('Edited message not found', hub.id)
      Backend.report(
        `Edited message not found ${hub.companyId} ${hub.id} ${editedMessageTimestamp} ${message}`
      )
      // TODO: fix this workaround
      ChatUtils.postMessage({hub, message, params, messageToReply})
      return
    }

    const text = HTMLUtil.messageTextHtmlToMessageText(message)
    if (HTMLUtil.hasComplexFormatting(message)) {
      msg.setTextHtml(message)
    } else {
      msg.clearTextHtml()
    }
    if (msg.text !== text) {
      msg.edited = {timestamp: Time.now()}
    }
    msg.text = text

    if (params?.chunks?.length) {
      ChatUtils.subscribeUsersFromChunks(hub, params.chunks)
      msg.properties.params = params
    }

    void Tracking.send(TrackingEvent.chatEditMessage)
    hub.commit()
  }

  static postOrUpdateMessage({
    hub,
    message,
    params,
    editedMessageTimestamp,
    messageToReply,
    isSystemUser
  }: PostOrUpdateMessage): ChatMessage | undefined {
    if (!Chat.beforePostMessage(hub, message)) {
      ClientLogger.skipMessage(hub, 'beforePostMessage')
      return undefined
    }

    const transformedMessage = Chat.beforePostMessageTransform(
      hub,
      message,
      !!editedMessageTimestamp
    )

    if (editedMessageTimestamp) {
      ChatUtils.updateMessage({
        hub,
        message: transformedMessage,
        params,
        messageToReply,
        editedMessageTimestamp
      })
      return undefined
    } else {
      return ChatUtils.postMessage({
        hub,
        message: transformedMessage,
        params,
        messageToReply,
        properties: {isSystemUser}
      })
    }
  }

  static async postOrUpdateMessageWithFiles({
    hub,
    message,
    params,
    editedMessageTimestamp,
    messageToReply,
    files,
    messageFileLinks = []
  }: PostOrUpdateMessageWithFiles): Promise<{filesSendFailed: boolean}> {
    let fileUrls: string[] = []

    if (files.length) {
      fileUrls = new Array(files.length)
      await Promise.all(
        files.map(async (file, i) => {
          const id = Util.UUID()
          FileUploadProgress.addFile(id, file)
          const url = await ClientUtilities.saveFile({file}, undefined, p =>
            FileUploadProgress.updateProgress(id, p)
          )
          FileUploadProgress.removeFile(id)
          if (url) {
            fileUrls[i] = FilePreview.encodeRelativeUrl(url)
          }
        })
      )
      fileUrls = fileUrls.filter(x => x)

      if (fileUrls.length !== files.length) {
        const shouldRetry = await showConfirm(
          Lang.msg.chat.fileUploadFailedTitle,
          false,
          true,
          {
            primaryBtnText: Lang.msg.chat.fileUploadFailedRetry,
            closeBtnText: Lang.msg.chat.fileUploadFailedCancel,
            title: Lang.msg.chat.fileUploadFailedTitle,
            subtitle: Lang.msg.chat.fileUploadFailedMessage
          }
        )

        if (shouldRetry) {
          return ChatUtils.postOrUpdateMessageWithFiles({
            hub,
            message,
            params,
            editedMessageTimestamp,
            messageToReply,
            files
          })
        } else {
          return {filesSendFailed: true}
        }
      }

      void Tracking.send(TrackingEvent.chatUploadFile)
    }

    const allFileLinks = [...fileUrls, ...messageFileLinks]

    if (allFileLinks.length > 0) {
      message = (message ? message + ' \n' : '') + allFileLinks.join(' ')
    }

    if (message) {
      ChatUtils.postOrUpdateMessage({
        hub,
        message,
        params,
        messageToReply,
        editedMessageTimestamp
      })
    }

    return {filesSendFailed: false}
  }
}
